import { useMemo } from 'react';

import { DateTime } from 'luxon';

import usePortfolio from '~/pages/Portfolio/usePortfolio';

import { useSimulationTable } from '../SimulationTable/withSimulationTable';

const usePeriods = () => {
  const { startYear, endYear, expandedPeriods } = usePortfolio();
  const { loading, simulationPeriods } = useSimulationTable();

  const periods = useMemo(() => {
    if (loading && simulationPeriods) {
      const filtered: string[] = [];
      const processedYears = new Set<string>();

      for (const { period } of simulationPeriods) {
        const isMonthly = period.includes('-');
        const year = isMonthly ? period.split('-')[0] : period;
        const isYearExpanded = expandedPeriods?.includes(year);

        if (isMonthly) {
          if (isYearExpanded) {
            filtered.push(period);
          }
        } else {
          filtered.push(period);
          processedYears.add(year);
        }
      }

      return {
        startYear,
        endYear,
        periods: filtered,
        fullPeriods: simulationPeriods.map(({ period }) => period),
      };
    }

    const periodsSet: Set<string> = new Set([]);
    const fullPeriods: Array<string> = [];
    const filtered: Array<string> = [];
    const processedYears = new Set<string>();

    const startDate = DateTime.fromObject({ year: Number(startYear) });
    const endDate = DateTime.fromObject({ year: Number(endYear), month: 12 });

    let currentMonth = startDate;

    while (currentMonth <= endDate) {
      const yearFormat = currentMonth.toFormat('yyyy');
      const monthFormat = currentMonth.toFormat('yyyy-MM');
      const isYearExpanded = expandedPeriods?.includes(yearFormat);

      periodsSet.add(monthFormat);
      fullPeriods.push(monthFormat);

      if (isYearExpanded) {
        filtered.push(monthFormat);
      }

      if (currentMonth.month === 12 && !processedYears.has(yearFormat)) {
        periodsSet.add(yearFormat);
        filtered.push(yearFormat);
        processedYears.add(yearFormat);
      }

      currentMonth = currentMonth.startOf('month').plus({ months: 1 });
    }

    return { startYear, endYear, periods: filtered, fullPeriods: Array.from(periodsSet) };
  }, [endYear, expandedPeriods, loading, simulationPeriods, startYear]);

  return periods;
};

export default usePeriods;
